import { DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Builder } from 'builder-pattern';
import { BehaviorSubject, catchError, first, map, Observable, of, switchMap, throwError } from 'rxjs';
import { ParamsHelper } from 'src/app/helpers/ParamsHelper';
import { removeNonDigitsCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';
import { resolvePsaSelectItemId } from 'src/app/helpers/utils/psa-baseline-methodology.util';
import { HandleErrorService } from 'src/app/services/handle-error/handle-error.service';
import { Pagination } from 'src/app/shared/interfaces/pagination';
import { 
  Attachment,
  Biomes,
  BiomesRercord,
  Developer,
  Locations,
  ProjectStatus, 
  ProjectUpdateStatus, 
  PsaProjectData, 
  PsaProjectFilter, 
  PsaProjectRegisterFormRegister, 
  PsaProjectRegisterRequest, 
  PsaProjectResponse, 
  PsaProjectsResponse 
} from 'src/app/shared/interfaces/psa-project-register';
import { PostApi } from 'src/app/shared/interfaces/response-api';
import { AppConfigService } from 'src/environments/app-config.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private environment: any
  private apiUrl: string;

  filters: BehaviorSubject<PsaProjectFilter> = new BehaviorSubject({} as PsaProjectFilter);
  toString = (value: any) => (value ? String(value) : '');

  defaultPagination: Pagination<PsaProjectData> = {
    page: 1,
    size: 10,
    results: [],
    total: 0
  };

  constructor(
    private httpClient: HttpClient,
    private datePipe: DatePipe,
    private appConfig: AppConfigService,
    private handleErrorService: HandleErrorService
  ) {
    this.environment = this.appConfig.config;
    this.apiUrl = `${this.environment.apiIssuer}/projects`
  }

  public getProjectById(id: number): Observable<PsaProjectResponse> {
    return this.httpClient.get<PsaProjectResponse>(`${this.apiUrl}/${id}`).pipe(
      first(),
      catchError(err => {      
        return this.handleErrorService.handleError(err)
      })
    );
  }

  public getProjects(psaProjectFilter: PsaProjectFilter, pagination?: Pagination<PsaProjectData>): Observable<PsaProjectsResponse> {
    const params = new ParamsHelper(new HttpParams())
      .setParams('page', this.toString(pagination?.page))
      .setParams('page-size', this.toString(pagination?.size))
      .setParams('status', this.toString(psaProjectFilter?.status))
      .setParams('projectName', this.toString(psaProjectFilter?.projectName))
      .getParams();

    return this.httpClient.get<PsaProjectsResponse>(`${this.apiUrl}`, {params}).pipe(
      first(),
      catchError(err => {      
        return this.handleErrorService.handleError(err)
      })
    );
  }

  public getProjectsConsult(
    psaProjectFilter: PsaProjectFilter,
    pagination?: Pagination<PsaProjectData>,
  ): Observable<PsaProjectsResponse> {
    const params = new ParamsHelper(new HttpParams())
      .setParams('page', this.toString(pagination?.page))
      .setParams('page-size', this.toString(pagination?.size))
      .setParams('status', this.toString(psaProjectFilter?.status))
      .setParams('projectName', this.toString(psaProjectFilter?.projectName))
      .getParams();

    return this.httpClient.get<PsaProjectsResponse>(`${this.apiUrl}/consult`, { params }).pipe(
      first(),
      catchError(err => {
        return this.handleErrorService.handleError(err);
      }),
    );
  }

  /**
   * Busca todas as páginas no front quando o back limita page-size (ex.: default 25).
   */
  public getAllProjects(
    psaProjectFilter: PsaProjectFilter,
    pageSize = 200,
  ): Observable<PsaProjectData[]> {
    return this.fetchAllProjectPages(psaProjectFilter, 1, pageSize, []);
  }

  private fetchAllProjectPages(
    psaProjectFilter: PsaProjectFilter,
    page: number,
    pageSize: number,
    accumulated: PsaProjectData[],
    chunkSize: number | null = null,
  ): Observable<PsaProjectData[]> {
    if (page > 200) {
      return of(accumulated);
    }

    return this.getProjects(psaProjectFilter, { page, size: pageSize, results: [] }).pipe(
      switchMap(response => {
        const batch = response?.data?.carbonProjects ?? [];

        if (!batch.length) {
          return of(accumulated);
        }

        const all = [...accumulated, ...batch];
        const totalPages = response?.meta?.totalPages;
        const totalRecords = response?.meta?.totalRecords;

        if (totalPages != null) {
          return page < totalPages
            ? this.fetchAllProjectPages(psaProjectFilter, page + 1, pageSize, all, chunkSize)
            : of(all);
        }

        if (totalRecords != null) {
          return all.length < totalRecords
            ? this.fetchAllProjectPages(psaProjectFilter, page + 1, pageSize, all, chunkSize)
            : of(all);
        }

        const resolvedChunkSize = chunkSize ?? batch.length;
        const isFullPage = resolvedChunkSize > 0 && batch.length >= resolvedChunkSize;

        if (!isFullPage) {
          return of(all);
        }

        return this.fetchAllProjectPages(
          psaProjectFilter,
          page + 1,
          pageSize,
          all,
          resolvedChunkSize,
        );
      }),
    );
  }

  public getProjectDraftById(id: number): Observable<PsaProjectResponse> {
    return this.httpClient.get<PsaProjectResponse>(`${this.apiUrl}/drafts/${id}`).pipe(
      first(),
      catchError(err => {      
        return this.handleErrorService.handleError(err)
      })
    );
  }

  public getProjectsDraft(psaProjectFilter?: PsaProjectFilter, pagination?: Pagination<PsaProjectData>): Observable<PsaProjectsResponse> {
    const params = new ParamsHelper(new HttpParams())
      .setParams('page', this.toString((pagination || this.defaultPagination).page))
      .setParams('page-size', this.toString((pagination || this.defaultPagination).size))
      .setParams('projectName', this.toString(psaProjectFilter?.projectName))
      .getParams();

    return this.httpClient.get<PsaProjectsResponse>(`${this.apiUrl}/drafts`, {params}).pipe(
      first(),
      catchError(err => {      
        return this.handleErrorService.handleError(err)
      })
    );
  }

  public projectDraftRegister(form: any): Observable<PsaProjectResponse> {
    const project = this.mapperProjectOrDraftFromForm(form);
    const body: PostApi<PsaProjectRegisterRequest> = {
      data: project
    }

    return this.httpClient.post<PsaProjectResponse>(`${this.apiUrl}/drafts`, body).pipe(
      first(),
      catchError(err => {
        return this.handleErrorService.handleError(err)
      })
    );
  }

  public cloneProjectToDraft(project: PsaProjectData): Observable<PsaProjectResponse> {
    const projectCopy = this.mapperProjectFromRequest(project);

    const body: PostApi<PsaProjectRegisterRequest> = {
      data: projectCopy
    }

    return this.httpClient.post<PsaProjectResponse>(`${this.apiUrl}/drafts`, body).pipe(
      first(),
      catchError(err => {
        return this.handleErrorService.handleError(err)
      })
    );
  }

  public projectDraftUpdate(form: any, id: number): Observable<PsaProjectResponse> {
    const project = this.mapperProjectOrDraftFromForm(form);
    const body: PostApi<PsaProjectRegisterRequest> = {
      data: project
    }

    return this.httpClient.put<PsaProjectResponse>(`${this.apiUrl}/drafts/${id}`, body).pipe(
      first(),
      catchError(err => {      
        return this.handleErrorService.handleError(err)
      })
    );
  }

  public projectDraftRegisterFinalize(form: any, id: number): Observable<PsaProjectResponse> {
    const project = this.mapperProjectOrDraftFromForm(form);
    const body: PostApi<PsaProjectRegisterRequest> = {
      data: project
    }

    return this.httpClient.post<PsaProjectResponse>(`${this.apiUrl}/drafts/${id}/finalize`, body).pipe(
      first(),
      catchError(err => {
        this.handleErrorService.handleError(err);
        return throwError(() => err);
      })
    );
  }

  public deleteProjectsDraft(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.apiUrl}/drafts/${id}`).pipe(
      first(),
      catchError(err => {
        this.handleErrorService.handleError(err);
        return throwError(() => err)
      })
    );
  }

  public updateProjectStatus(id: number, data: ProjectUpdateStatus): Observable<any> {
    const body: PostApi<ProjectUpdateStatus> = {
      data: data
    }

    return this.httpClient.post<any>(`${this.apiUrl}/status/${id}`, body).pipe(
      first(),
      catchError(err => {
        return this.handleErrorService.handleError(err)
      })
    );
  }

  public mapperProjectOrDraftFromForm(formRegister: PsaProjectRegisterFormRegister): PsaProjectRegisterRequest {
    return Builder<PsaProjectRegisterRequest>()
        .projectName(formRegister.projectName || '')
        .registerCode(formRegister.registerCode || '')
        .projectStartDate(this.datePipe.transform(formRegister.projectStartDate, 'yyyy-MM-dd') || null)
        .baselineActivityId(resolvePsaSelectItemId(formRegister.baselineActivity))
        .generalDetails(formRegister.generalDetails || '')
        .developers(this.sanitizeDevelopers(formRegister.developers))

        .locations(this.mapWithParseLinkFromLocation(formRegister.locations) || [])

        .methodologyId(resolvePsaSelectItemId(formRegister.methodology))
        .methodologyReportLink(formRegister.ramComplianceReportLink || "")
        .methodologyDocumentDescription(formRegister.ramComplianceReportDetail || "")
        .otherDocumentationDescription(formRegister.documentDescription || "")
        .attachments(this.mapWithParseLinkFromAttachments(formRegister.attachments) || [])

        .odsNumbers(formRegister.odsNumbers || [1])
        .coBenefits(formRegister.coBenefits || '')
      .build()
  }

  public mapperProjectFromRequest(project: PsaProjectData): PsaProjectRegisterRequest {
    return Builder<PsaProjectRegisterRequest>()
        .projectName(project.projectName || '')
        .registerCode(project.registerCode || '')
        .projectStartDate(this.datePipe.transform(project.projectStartDate, 'yyyy-MM-dd') || null)
        .baselineActivityId(project.baselineActivityId)
        .generalDetails(project.generalDetails || '')
        .developers(this.sanitizeDevelopers(project.developers))

        .locations(this.mapWithParseLinkFromLocation(project.locations) || [])

        .methodologyId(project.methodologyId)
        .methodologyReportLink(project.methodologyReportLink || "")
        .methodologyDocumentDescription(project.methodologyDocumentDescription || "")
        .otherDocumentationDescription(project.otherDocumentationDescription || "")
        .attachments(this.mapWithParseLinkFromAttachments(project.attachments) || [])

        .odsNumbers(project.odsNumbers || [1])
        .coBenefits(project.coBenefits || '')
      .build()
  }

  parseProjectStatus(project: PsaProjectData): PsaProjectData {
    switch (project.status) {
      case 'PROJECT_PENDING_AUDITOR_VALIDATION':
        project.status = ProjectStatus.PROJECT_PENDING_AUDITOR_VALIDATION;
        break;
      case 'PROJECT_APPROVED_BY_AUDITOR':
        project.status = ProjectStatus.PROJECT_APPROVED_BY_AUDITOR;
        break;
      case 'PROJECT_REJECTED_BY_AUDITOR':
        project.status = ProjectStatus.PROJECT_REJECTED_BY_AUDITOR;
        break;
      case 'PROJECT_ADJUSTED_BY_DEVELOPER':
        project.status = ProjectStatus.PROJECT_ADJUSTED_BY_DEVELOPER;
        break;
      case 'PROJECT_PENDING_DEVELOPER_CONFIRMATION':
        project.status = ProjectStatus.PROJECT_PENDING_DEVELOPER_CONFIRMATION
        break;
      case 'PROJECT_CONFIRMED_BY_DEVELOPER':
        project.status = ProjectStatus.PROJECT_CONFIRMED_BY_DEVELOPER;
        break;
      case 'PROJECT_REJECTED_BY_DEVELOPER':
        project.status = ProjectStatus.PROJECT_REJECTED_BY_DEVELOPER;
        break;
      case 'PROJECT_REGISTERED_IN_B3':
        project.status = ProjectStatus.PROJECT_REGISTERED_IN_B3;
        break;
    }

    return project;
  }

  mapWithParseLinkFromLocation(locations?: Locations[] | null) {
    return locations?.map((location) => ({
      ...location,
      landOwnerCpfCnpj: location.landOwnerCpfCnpj
        ? removeNonDigitsCNPJ(String(location.landOwnerCpfCnpj))
        : location.landOwnerCpfCnpj,
    }));
  }

  private sanitizeDevelopers(developers?: Developer[] | null): Developer[] {
    return (developers ?? []).map((developer) => ({
      ...developer,
      cpfCnpj: developer.cpfCnpj
        ? removeNonDigitsCNPJ(developer.cpfCnpj)
        : developer.cpfCnpj,
    }));
  }

  mapWithParseLinkFromAttachments(attachments?: Attachment[] | null) {
    return attachments?.map((attachment) => {
      // formata link
      if (attachment.link) {
        attachment.link = attachment.link;
      }
      return attachment;
    })
  }
}
